// Iframe Integration Message Types
// VAN-021: Comprehensive postMessage API for iframe communication

// Типы источников согласно архитектуре приложения
export type IframeSource = "preorder" | "qr" | "delivery";

// Типы тем для синхронизации
export type ThemeMode = "light" | "dark" | "auto" | "custom";

// Базовый интерфейс для всех сообщений iframe
export interface IframeMessage {
  type: MessageType;
  payload?: any;
  timestamp: number;
  source: IframeSource;
}

// Типы сообщений iframe
export type MessageType =
  | "PREORDER_APP_READY"
  | "PREORDER_COMPLETED"
  | "PREORDER_CANCELLED"
  | "PREORDER_ERROR"
  | "THEME_CHANGED"
  | "REQUEST_CLOSE";

// Данные готовности приложения
export interface AppReadyData {
  source: IframeSource;
  route: string;
  brandId?: string;
  pointId?: string;
  tableId?: string; // Только для QR режима
  capabilities: string[]; // Поддерживаемые функции: ["ordering", "theming", "closing"]
}

// Данные заказа согласно схеме OrderItem
export interface OrderData {
  orderId: string;
  items: Array<{
    uuid: string; // Используем uuid как в OrderItem
    name: string;
    quantity: number;
    price: number; // Цена в копейках для точности
    modifiers?: Array<{
      id: string;
      name: string;
      price: number; // Цена в копейках
      type: "addon" | "upgrade" | "customization";
    }>;
    comment?: string;
  }>;
  sum: number; // Общая сумма в копейках
  comment?: string;
  brandId: string;
  pointId: string;
  timestamp: number;
  type: "PREORDER" | "DELIVERY" | "PICKUP";
}

// Данные изменения темы
export interface ThemeChangeData {
  theme: ThemeMode;
  colors?: {
    primary?: string;
    secondary?: string;
    background?: string;
    surface?: string;
    accent?: string;
    text?: string;
    textSecondary?: string;
    border?: string;
    success?: string;
    warning?: string;
    error?: string;
    info?: string;
  };
  // Полная кастомная цветовая схема в JSON
  customTheme?: {
    palette: Record<string, any>;
    typography?: Record<string, any>;
    spacing?: Record<string, any>;
    shadows?: Record<string, any>;
  };
}

// Данные запроса закрытия
export interface CloseRequestData {
  reason: "user_action" | "error" | "completion" | "cancel";
  message?: string;
}

// Данные ошибки
export interface ErrorData {
  error: string;
  code?: string;
  details?: any;
}

// Типизированные сообщения для каждого типа
export interface AppReadyMessage extends IframeMessage {
  type: "PREORDER_APP_READY";
  payload: AppReadyData;
}

export interface OrderCompletedMessage extends IframeMessage {
  type: "PREORDER_COMPLETED";
  payload: OrderData;
}

export interface OrderCancelledMessage extends IframeMessage {
  type: "PREORDER_CANCELLED";
  payload?: undefined;
}

export interface OrderErrorMessage extends IframeMessage {
  type: "PREORDER_ERROR";
  payload: ErrorData;
}

export interface ThemeChangedMessage extends IframeMessage {
  type: "THEME_CHANGED";
  payload: ThemeChangeData;
}

export interface CloseRequestMessage extends IframeMessage {
  type: "REQUEST_CLOSE";
  payload: CloseRequestData;
}

// Union type для всех возможных сообщений
export type TypedIframeMessage =
  | AppReadyMessage
  | OrderCompletedMessage
  | OrderCancelledMessage
  | OrderErrorMessage
  | ThemeChangedMessage
  | CloseRequestMessage;

// Сообщения от родителя к iframe
export interface ParentToIframeMessage {
  type: "SET_THEME" | "PARENT_READY";
  payload?: any;
  timestamp: number;
}

// Функция валидации сообщений
export const isValidIframeMessage = (data: any): data is IframeMessage => {
  return (
    data &&
    typeof data === "object" &&
    typeof data.type === "string" &&
    [
      "PREORDER_APP_READY",
      "PREORDER_COMPLETED",
      "PREORDER_CANCELLED",
      "PREORDER_ERROR",
      "THEME_CHANGED",
      "REQUEST_CLOSE",
    ].includes(data.type) &&
    typeof data.timestamp === "number" &&
    ["preorder", "qr", "delivery"].includes(data.source)
  );
};

// Функция валидации сообщений от родителя
export const isValidParentMessage = (
  data: any
): data is ParentToIframeMessage => {
  return (
    data &&
    typeof data === "object" &&
    typeof data.type === "string" &&
    ["SET_THEME", "PARENT_READY"].includes(data.type) &&
    typeof data.timestamp === "number"
  );
};

// Утилита для создания сообщений с timestamp
export const createIframeMessage = <T extends MessageType>(
  type: T,
  source: IframeSource,
  payload?: any
): IframeMessage => ({
  type,
  payload,
  timestamp: Date.now(),
  source,
});

// Утилита для создания сообщений от родителя
export const createParentMessage = (
  type: "SET_THEME" | "PARENT_READY",
  payload?: any
): ParentToIframeMessage => ({
  type,
  payload,
  timestamp: Date.now(),
});
